// app/components/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/Steps.tsx
"use client";

import * as React from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box, Grid } from "@mui/material";
import Image from "next/image";
import { useTranslations, useLocale } from "next-intl";
import { usePathname } from "next/navigation";
import ExpertLoadingSolutions from "@/app/components/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/ExpertLoadingSolutions";
import { renderWithLineBreaks } from "@/app/utils/renderWithLineBreaks";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string;
};

const STEP_KEYS = [
  "ourservices.steps.step1.step",
  "ourservices.steps.step1.title",
  "ourservices.steps.step1.content",
  "ourservices.steps.step2.step",
  "ourservices.steps.step2.title",
  "ourservices.steps.step2.content",
  "ourservices.steps.step3.step",
  "ourservices.steps.step3.title",
  "ourservices.steps.step3.content",
  "ourservices.steps.step4.step",
  "ourservices.steps.step4.title",
  "ourservices.steps.step4.content",
  "ourservices.steps.step5.step",
  "ourservices.steps.step5.title",
  "ourservices.steps.step5.content",
  "ourservices.steps.step6.step",
  "ourservices.steps.step6.title",
  "ourservices.steps.step6.content",
  "ourservices.steps.step1.image",
  "ourservices.steps.step2.image",
  "ourservices.steps.step3.image",
  "ourservices.steps.step4.image",
  "ourservices.steps.step5.image",
  "ourservices.steps.step6.image",
] as const;

type StepKey = (typeof STEP_KEYS)[number];

export default function Steps() {
  const t = useTranslations("OurServices");
  const locale = useLocale();
  const pathname = usePathname() || "/";

  const stepFixedBgrRef = React.useRef<HTMLDivElement>(null);

  // map pathname → CMS path (ตัด prefix /en, /zh)
  const pagePath = React.useMemo(() => {
    const parts = pathname.split("/");
    if (parts[1] === "en" || parts[1] === "zh") {
      const tail = parts.slice(2).join("/");
      return tail ? `/${tail}` : "/";
    }
    return pathname || "/";
  }, [pathname]);

  // ---- default values (จาก i18n + static image src) ----
  const buildDefaultMap = React.useCallback((): Record<StepKey, string> => {
    return {
      // step1
      "ourservices.steps.step1.step": t("Steps.step1.step"),
      "ourservices.steps.step1.title": t("Steps.step1.title"),
      "ourservices.steps.step1.content": t("Steps.step1.content"),
      // step2
      "ourservices.steps.step2.step": t("Steps.step2.step"),
      "ourservices.steps.step2.title": t("Steps.step2.title"),
      "ourservices.steps.step2.content": t("Steps.step2.content"),
      // step3
      "ourservices.steps.step3.step": t("Steps.step3.step"),
      "ourservices.steps.step3.title": t("Steps.step3.title"),
      "ourservices.steps.step3.content": t("Steps.step3.content"),
      // step4
      "ourservices.steps.step4.step": t("Steps.step4.step"),
      "ourservices.steps.step4.title": t("Steps.step4.title"),
      "ourservices.steps.step4.content": t("Steps.step4.content"),
      // step5
      "ourservices.steps.step5.step": t("Steps.step5.step"),
      "ourservices.steps.step5.title": t("Steps.step5.title"),
      "ourservices.steps.step5.content": t("Steps.step5.content"),
      // step6
      "ourservices.steps.step6.step": t("Steps.step6.step"),
      "ourservices.steps.step6.title": t("Steps.step6.title"),
      "ourservices.steps.step6.content": t("Steps.step6.content"),

      // images default
      "ourservices.steps.step1.image": "/assets/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/steps/img1.png",
      "ourservices.steps.step2.image": "/assets/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/steps/img2.png",
      "ourservices.steps.step3.image": "/assets/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/steps/img3.png",
      "ourservices.steps.step4.image": "/assets/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/steps/img4.png",
      "ourservices.steps.step5.image": "/assets/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/steps/img5.png",
      "ourservices.steps.step6.image": "/assets/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/steps/img6.png",
    };
  }, [t]);

  const [inline, setInline] = React.useState<Record<string, string>>(
    () => buildDefaultMap()
  );

  // fixed background scroll effect (layout เดิม)
  React.useEffect(() => {
    const handleScroll = () => {
      if (!stepFixedBgrRef.current) return;
      const introTop = stepFixedBgrRef.current.getBoundingClientRect().top;
      if (introTop <= 0) {
        stepFixedBgrRef.current.classList.add("bg-fixed");
      } else {
        stepFixedBgrRef.current.classList.remove("bg-fixed");
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // reset default เมื่อ locale เปลี่ยน
  React.useEffect(() => {
    setInline(buildDefaultMap());
  }, [locale, buildDefaultMap]);

  // //
  React.useEffect(() => {
    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID}&path=${encodeURIComponent(
            pagePath
          )}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;
        const map: Record<string, string> = {};

        list.forEach((e) => {
          const key =
            e.cmsKey ||
            e.selector?.match(/data-cms-key="([^"]+)"/)?.[1] ||
            undefined;
          if (key) {
            map[key] = e.value;
          }
        });

        if (Object.keys(map).length > 0) {
          setInline((prev) => ({ ...prev, ...map }));
        }
      } catch (err) {
        console.warn("[OurServices Steps] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [locale, pagePath]);

  // helper ดึงข้อความจาก inline map
  const txt = (key: StepKey) => inline[key] ?? "";

  return (
    <Box className="our-services-steps" ref={stepFixedBgrRef} sx={{
      pt: 10
    }}>
      <Container className="container" sx={{ mb: 10 }}>
        {/* STEP 1 */}
        <Box className="our-services-steps-box odd" data-aos="fade-up" sx={{
          mb: 3
        }}>
          <Grid
            container
            spacing={{ xs: 3, md: 3 }}
            sx={{
              display: "flex",
              alignItems: "center"
            }}>
            <Grid
              size={{ xs: 12, md: 5, lg: 3.5 }}
              sx={{
                display: "flex",
                justifyContent: "center"
              }}>
              <Image
                src={txt("ourservices.steps.step1.image")}
                title="our-services step1"
                alt="our-services step1"
                width={400}
                height={244}
                className="img-fluid"
                data-cms-key="ourservices.steps.step1.image"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 7, lg: 8.5 }}>
              <Typography
                variant="h3"
                data-aos="fade-up"
                sx={{ whiteSpace: { xs: "normal", lg: "pre-line" } }}
                data-cms-key="ourservices.steps.step1.step"
              >
                {renderWithLineBreaks(txt("ourservices.steps.step1.step"))}
              </Typography>
              <Typography
                variant="h4"
                data-aos="fade-up"
                data-cms-key="ourservices.steps.step1.title"
                sx={{
                  my: 2,
                  whiteSpace: { xs: "normal", lg: "pre-line" }
                }}>
                {renderWithLineBreaks(txt("ourservices.steps.step1.title"))}
              </Typography>
              <Typography
                variant="body1"
                data-aos="fade-up"
                sx={{ whiteSpace: { xs: "normal", lg: "pre-line" } }}
                data-cms-key="ourservices.steps.step1.content"
              >
                {renderWithLineBreaks(txt("ourservices.steps.step1.content"))}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* STEP 2 */}
        <Box className="our-services-steps-box even" data-aos="fade-up" sx={{
          mb: 3
        }}>
          <Grid
            container
            spacing={{ xs: 3, md: 3 }}
            sx={{
              display: "flex",
              alignItems: "center"
            }}>
            <Grid
              size={{ xs: 12, md: 5, lg: 3.5 }}
              sx={{
                display: "flex",
                justifyContent: "center"
              }}>
              <Image
                src={txt("ourservices.steps.step2.image")}
                title="our-services step 2"
                alt="our-services step 2"
                width={400}
                height={244}
                className="img-fluid"
                data-cms-key="ourservices.steps.step2.image"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 7, lg: 8.5 }}>
              <Typography
                variant="h3"
                data-aos="fade-up"
                sx={{ whiteSpace: { xs: "normal", lg: "pre-line" } }}
                data-cms-key="ourservices.steps.step2.step"
              >
                {renderWithLineBreaks(txt("ourservices.steps.step2.step"))}
              </Typography>
              <Typography
                variant="h4"
                data-aos="fade-up"
                data-cms-key="ourservices.steps.step2.title"
                sx={{
                  my: 2,
                  whiteSpace: { xs: "normal", lg: "pre-line" }
                }}>
                {renderWithLineBreaks(txt("ourservices.steps.step2.title"))}
              </Typography>
              <Typography
                variant="body1"
                data-aos="fade-up"
                sx={{ whiteSpace: { xs: "normal", lg: "pre-line" } }}
                data-cms-key="ourservices.steps.step2.content"
              >
                {renderWithLineBreaks(txt("ourservices.steps.step2.content"))}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* STEP 3 */}
        <Box className="our-services-steps-box odd" data-aos="fade-up" sx={{
          mb: 3
        }}>
          <Grid
            container
            spacing={{ xs: 3, md: 3 }}
            sx={{
              display: "flex",
              alignItems: "center"
            }}>
            <Grid
              size={{ xs: 12, md: 5, lg: 3.5 }}
              sx={{
                display: "flex",
                justifyContent: "center"
              }}>
              <Image
                src={txt("ourservices.steps.step3.image")}
                title="our-services step 3"
                alt="our-services step 3"
                width={400}
                height={244}
                className="img-fluid"
                data-cms-key="ourservices.steps.step3.image"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 7, lg: 8.5 }}>
              <Typography
                variant="h3"
                data-aos="fade-up"
                sx={{ whiteSpace: { xs: "normal", lg: "pre-line" } }}
                data-cms-key="ourservices.steps.step3.step"
              >
                {renderWithLineBreaks(txt("ourservices.steps.step3.step"))}
              </Typography>
              <Typography
                variant="h4"
                data-aos="fade-up"
                data-cms-key="ourservices.steps.step3.title"
                sx={{
                  my: 2,
                  whiteSpace: { xs: "normal", lg: "pre-line" }
                }}>
                {renderWithLineBreaks(txt("ourservices.steps.step3.title"))}
              </Typography>
              <Typography
                variant="body1"
                data-aos="fade-up"
                sx={{ whiteSpace: { xs: "normal", lg: "pre-line" } }}
                data-cms-key="ourservices.steps.step3.content"
              >
                {renderWithLineBreaks(txt("ourservices.steps.step3.content"))}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* STEP 4 */}
        <Box className="our-services-steps-box even" data-aos="fade-up" sx={{
          mb: 3
        }}>
          <Grid
            container
            spacing={{ xs: 3, md: 3 }}
            sx={{
              display: "flex",
              alignItems: "center"
            }}>
            <Grid
              size={{ xs: 12, md: 5, lg: 3.5 }}
              sx={{
                display: "flex",
                justifyContent: "center"
              }}>
              <Image
                src={txt("ourservices.steps.step4.image")}
                title="our services steps 4"
                alt="our services steps 4"
                width={400}
                height={244}
                className="img-fluid"
                data-cms-key="ourservices.steps.step4.image"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 7, lg: 8.5 }}>
              <Typography
                variant="h3"
                data-aos="fade-up"
                sx={{ whiteSpace: { xs: "normal", lg: "pre-line" } }}
                data-cms-key="ourservices.steps.step4.step"
              >
                {renderWithLineBreaks(txt("ourservices.steps.step4.step"))}
              </Typography>
              <Typography
                variant="h4"
                data-aos="fade-up"
                data-cms-key="ourservices.steps.step4.title"
                sx={{
                  my: 2,
                  whiteSpace: { xs: "normal", lg: "pre-line" }
                }}>
                {renderWithLineBreaks(txt("ourservices.steps.step4.title"))}
              </Typography>
              <Typography
                variant="body1"
                data-aos="fade-up"
                sx={{ whiteSpace: { xs: "normal", lg: "pre-line" } }}
                data-cms-key="ourservices.steps.step4.content"
              >
                {renderWithLineBreaks(txt("ourservices.steps.step4.content"))}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* STEP 5 */}
        <Box className="our-services-steps-box odd" data-aos="fade-up" sx={{
          mb: 3
        }}>
          <Grid
            container
            spacing={{ xs: 3, md: 3 }}
            sx={{
              display: "flex",
              alignItems: "center"
            }}>
            <Grid
              size={{ xs: 12, md: 5, lg: 3.5 }}
              sx={{
                display: "flex",
                justifyContent: "center"
              }}>
              <Image
                src={txt("ourservices.steps.step5.image")}
                title="our-services step 5"
                alt="our-services step 5"
                width={400}
                height={244}
                className="img-fluid"
                data-cms-key="ourservices.steps.step5.image"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 7, lg: 8.5 }}>
              <Typography
                variant="h3"
                data-aos="fade-up"
                sx={{ whiteSpace: { xs: "normal", lg: "pre-line" } }}
                data-cms-key="ourservices.steps.step5.step"
              >
                {renderWithLineBreaks(txt("ourservices.steps.step5.step"))}
              </Typography>
              <Typography
                variant="h4"
                data-aos="fade-up"
                data-cms-key="ourservices.steps.step5.title"
                sx={{
                  my: 2,
                  whiteSpace: { xs: "normal", lg: "pre-line" }
                }}>
                {renderWithLineBreaks(txt("ourservices.steps.step5.title"))}
              </Typography>
              <Typography
                variant="body1"
                data-aos="fade-up"
                sx={{ whiteSpace: { xs: "normal", lg: "pre-line" } }}
                data-cms-key="ourservices.steps.step5.content"
              >
                {renderWithLineBreaks(txt("ourservices.steps.step5.content"))}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* STEP 6 */}
        <Box className="our-services-steps-box even" data-aos="fade-up" sx={{
          mb: 3
        }}>
          <Grid
            container
            spacing={{ xs: 3, md: 3 }}
            sx={{
              display: "flex",
              alignItems: "center"
            }}>
            <Grid
              size={{ xs: 12, md: 5, lg: 3.5 }}
              sx={{
                display: "flex",
                justifyContent: "center"
              }}>
              <Image
                src={txt("ourservices.steps.step6.image")}
                title="our-services step 6"
                alt="our-services step 6"
                width={400}
                height={244}
                className="img-fluid"
                data-cms-key="ourservices.steps.step6.image"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 7, lg: 8.5 }}>
              <Typography
                variant="h3"
                data-aos="fade-up"
                sx={{ whiteSpace: { xs: "normal", lg: "pre-line" } }}
                data-cms-key="ourservices.steps.step6.step"
              >
                {renderWithLineBreaks(txt("ourservices.steps.step6.step"))}
              </Typography>
              <Typography
                variant="h4"
                data-aos="fade-up"
                data-cms-key="ourservices.steps.step6.title"
                sx={{
                  my: 2,
                  whiteSpace: { xs: "normal", lg: "pre-line" }
                }}>
                {renderWithLineBreaks(txt("ourservices.steps.step6.title"))}
              </Typography>
              <Typography
                variant="body1"
                data-aos="fade-up"
                sx={{ whiteSpace: { xs: "normal", lg: "pre-line" } }}
                data-cms-key="ourservices.steps.step6.content"
              >
                {renderWithLineBreaks(txt("ourservices.steps.step6.content"))}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Container>
      <ExpertLoadingSolutions />
    </Box>
  );
}